import { nextTick, markRaw } from "vue"
import uniqueId from "lodash/uniqueId"

function removeModal(modalStack, modalInfo) {
    const idx = modalStack.indexOf(modalInfo)
    if (idx !== -1) {
        modalStack.splice(idx, 1)
    }
    modalInfo.onClose?.()
    if (modalInfo.focus) {
        nextTick(() => {
            // Do this after the modal has been destroyed,
            // so its focus handler doesn't kick in.
            modalInfo.restoreFocusEl?.focus()
        })
    }
}

export default {
    data() {
        return {
            modalStack: [],
        }
    },

    provide() {
        return {
            pushModal: (cmp, options) => this.pushModal(cmp, options),
            unshiftModal: (cmp, options) => this.unshiftModal(cmp, options),
            popModal: () => this.popModal(),
        }
    },

    methods: {
        /**
         * Show a component as a modal.
         * Adds it to the top of the modal stack.
         *
         * * props:          Props to pass to the component.
         * * on:             Listeners to pass to the component.
         * * key:            Optional. A key for the ModalLayer component.
         * * klass:          A class for the component.
         * * layerClass:     A class for the ModalLayer component the modal is shown inside of.
         * * focus:          Give focus to the modal, keep it inside and restore it when closed.
         * * onClickOutside: A function to be called when clicking outside the modal.
         *                   Defaults to removing the modal.
         *                   You probably want to use `onClose`.
         * * onEscape:       A function to be called when pressing Escape.
         *                   Defaults to removing the modal.
         *                   You probably want to use `onClose`.
         * * onClose:        A function to be called when the modal is closed.
         * * isPersistent:   Whether to close this modal when the current URL changes.
         * * skipTrappingFocusWhenThisElementGetsFocus:
         *                   Don't run the focus trapping logic if the focus goes to this element.
         *                   You probably don't want this.
         *                   Consult the team if you're going to use it.
         *
         * returns: A function that removes the modal when called.
         */
        pushModal(
            component,
            {
                props = {},
                on = {},
                key = uniqueId("modal-"),
                klass,
                layerClass,
                focus = true,
                skipTrappingFocusWhenThisElementGetsFocus,
                onClickOutside,
                onEscape,
                onClose,
                isPersistent = false,
            } = {}
        ) {
            const modalInfo = {
                restoreFocusEl: document.activeElement,
                component: markRaw(component),
                isPersistent,
                key,
                klass,
                layerClass: layerClass ?? "bg--black--50",
                on,
                focus,
                skipTrappingFocusWhenThisElementGetsFocus,
                onClickOutside:
                    onClickOutside ??
                    (event => {
                        event.preventDefault()
                        event.stopPropagation()
                        removeModal(this.modalStack, modalInfo)
                    }),
                onEscape:
                    onEscape ??
                    (event => {
                        event.stopPropagation()
                        removeModal(this.modalStack, modalInfo)
                    }),
                onClose,
                props,
            }
            this.modalStack.push(modalInfo)
            return () => removeModal(this.modalStack, modalInfo)
        },

        /**
         * Show a component as a modal.
         * Adds it to the bottom of the modal stack.
         *
         * Same options as `pushModal`.
         *
         * You probably want to use `pushModal`.
         */
        unshiftModal(
            component,
            {
                props = {},
                on = {},
                key = undefined,
                klass,
                layerClass,
                focus = true,
                onClickOutside,
                onEscape,
                isPersistent = false,
            } = {}
        ) {
            const modalInfo = {
                restoreFocusEl: document.activeElement,
                component: markRaw(component),
                isPersistent,
                key,
                klass,
                layerClass: layerClass ?? "bg--black--50",
                on,
                focus,
                onClickOutside:
                    onClickOutside ??
                    (event => {
                        event.preventDefault()
                        event.stopPropagation()
                        removeModal(this.modalStack, modalInfo)
                    }),
                onEscape:
                    onEscape ??
                    (event => {
                        event.stopPropagation()
                        removeModal(this.modalStack, modalInfo)
                    }),
                props,
            }
            this.modalStack.unshift(modalInfo)
            return () => removeModal(this.modalStack, modalInfo)
        },

        /**
         * Removes the topmost modal, if any.
         */
        popModal() {
            const modalInfo = this.modalStack.pop()
            if (!modalInfo) return

            modalInfo.onClose?.()
            if (modalInfo.focus) {
                this.$nextTick(() => {
                    // Do this after the modal has been destroyed,
                    // so its focus handler doesn't kick in.
                    modalInfo.restoreFocusEl?.focus()
                })
            }
        },
    },
}
